/**
 * Configuracoes.jsx - Scriptus Pharma Partner
 * VERSÃO REDESENHADA - TIPOGRAFIA COMPACTA
 * ✅ Alinhado com padrões de CatalogoProdutos.jsx
 * ✅ Fontes menores e interface mais densa
 * ✅ Glassmorphism Premium Refinado
 * ✅ CORRIGIDO: Espaçamento da sidebar lateral
 * ✅ CORRIGIDO v2: Atualiza geoPoint quando endereço é alterado
 * ✅ Todas funcionalidades preservadas
 */

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { getAuth, signOut, updatePassword, reauthenticateWithCredential, EmailAuthProvider, deleteUser } from "firebase/auth";
import { doc, getDoc, deleteDoc } from "firebase/firestore";
import { db } from "../config/firebase";
import { useNavigate } from "react-router-dom";
import { fetchWithAuth } from "../utils/authHelpers";
import HeaderMenu from "../components/HeaderMenu";
import { 
  Building2, 
  Lock, 
  Trash2, 
  Shield, 
  AlertTriangle, 
  Save, 
  Loader2, 
  Menu, 
  X,
  Info,
  Mail,
  Phone,
  FileCheck,
  MapPin,
  Hash,
  Building,
  Map,
  CheckCircle2
} from "lucide-react";

// ============================================
// COMPONENTES AUXILIARES MEMOIZADOS
// ============================================

const InputCustom = React.memo(({ label, type = "text", placeholder, value, onChange, disabled = false, icon: Icon, hint, required = false }) => (
  <div className="space-y-1.5">
    <label className="block sf-pro-text text-xs font-medium text-slate-700">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <div className="relative">
      {Icon && (
        <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
      )}
      <input
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        disabled={disabled}
        className={`input-glass-refined w-full ${Icon ? 'pl-9' : ''} ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
      />
    </div>
    {hint && (
      <p className="text-[10px] text-slate-500 sf-pro-text">{hint}</p>
    )}
  </div>
));

const BotaoCustom = React.memo(({ children, onClick, disabled = false, variant = "primary", loading = false }) => {
  const variants = {
    primary: "bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white",
    warning: "bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white",
    danger: "bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white"
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled || loading}
      className={`button-primary-refined ${variants[variant]} ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
    >
      {loading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
      {children}
    </button>
  );
});

// Lista de UFs brasileiras
const UF_LIST = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 
  'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 
  'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'
];

// ============================================
// HELPERS DE LEITURA (Compatibilidade)
// ============================================

function extrairDadosFarmacia(firestoreData) {
  const cd = firestoreData.companyData || {};
  const addr = cd.address || firestoreData.address || {};
  
  return {
    name: cd.name || firestoreData.name || '',
    tradeName: cd.tradeName || firestoreData.tradeName || '',
    cnpj: cd.cnpj || firestoreData.cnpj || '',
    crf: cd.crf || firestoreData.crf || '',
    phone: cd.phone || firestoreData.phone || '',
    afe: cd.afe || firestoreData.afe || '',
    
    address: {
      street: addr.street || '',
      number: addr.number || '',
      complement: addr.complement || '',
      neighborhood: addr.neighborhood || '',
      city: addr.city || '',
      state: addr.state || '',
      zipCode: addr.zipCode || ''
    },
    
    _meta: {
      uid: firestoreData.uid,
      email: firestoreData.email,
      plano: firestoreData.plano || 'basic',
      statusAssinatura: firestoreData.statusAssinatura || 'ativa',
      criadoEm: firestoreData.criadoEm
    }
  };
}

/**
 * ✅ REFATORADO: Prepara dados no formato esperado pelo backend PUT /profile
 * O backend cuida de: formatação WhatsApp, geocodificação, timestamps
 */
function prepararDadosParaAPI(farmacia) {
  return {
    companyData: {
      name: farmacia.name?.trim() || '',
      tradeName: farmacia.tradeName?.trim() || '',
      phone: farmacia.phone?.replace(/\D/g, '') || '', // Remove formatação para o backend formatar
      afe: farmacia.afe?.trim() || '',
      address: {
        street: farmacia.address?.street?.trim() || '',
        number: farmacia.address?.number?.trim() || '',
        complement: farmacia.address?.complement?.trim() || '',
        neighborhood: farmacia.address?.neighborhood?.trim() || '',
        city: farmacia.address?.city?.trim() || '',
        state: farmacia.address?.state || '',
        zipCode: farmacia.address?.zipCode?.replace(/\D/g, '') || '' // Remove formatação
      }
    },
    settings: {
      whatsappNumber: farmacia.phone?.replace(/\D/g, '') || '' // Backend adiciona código 55
    }
  };
}

// ============================================
// COMPONENTE PRINCIPAL
// ============================================

const Configuracoes = () => {
  const [farmacia, setFarmacia] = useState({
    name: '',
    tradeName: '',
    cnpj: '',
    crf: '',
    phone: '',
    afe: '',
    address: {
      street: '',
      number: '',
      complement: '',
      neighborhood: '',
      city: '',
      state: '',
      zipCode: ''
    },
    _meta: {
      uid: null,
      email: '',
      plano: 'basic',
      statusAssinatura: 'ativa',
      criadoEm: null
    }
  });
  
  const [mensagem, setMensagem] = useState("");
  const [tipoMensagem, setTipoMensagem] = useState("success");
  const [novaSenha, setNovaSenha] = useState("");
  const [senhaAtual, setSenhaAtual] = useState("");
  const [secaoSelecionada, setSecaoSelecionada] = useState("perfil");
  const [loadingSalvar, setLoadingSalvar] = useState(false);
  const [loadingLogin, setLoadingLogin] = useState(false);
  const [loadingExcluir, setLoadingExcluir] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [scrolled, setScrolled] = useState(false);
  const [sidebarMobile, setSidebarMobile] = useState(false);
  const [dadosOriginais, setDadosOriginais] = useState(null);
  
  const navigate = useNavigate();
  const auth = getAuth();
  const [user, setUser] = useState(null);

  // ============================================
  // FUNÇÕES AUXILIARES MEMOIZADAS
  // ============================================

  const exibirMensagem = useCallback((texto, tipo = "success") => {
    setMensagem(texto);
    setTipoMensagem(tipo);
    setTimeout(() => setMensagem(""), 4000);
  }, []);

  // Efeito de scroll otimizado com throttle
  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          setScrolled(window.scrollY > 10);
          ticking = false;
        });
        ticking = true;
      }
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  // ============================================
  // CARREGAMENTO DE DADOS
  // ============================================
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (currentUser) => {
      if (currentUser && !isInitialized) {
        setUser(currentUser);
        setIsLoading(true);
        
        try {
          const docRef = doc(db, "pharmacies", currentUser.uid);
          const docSnap = await getDoc(docRef);
          
          if (docSnap.exists()) {
            const firestoreData = docSnap.data();
            const dadosExtraidos = extrairDadosFarmacia(firestoreData);
            
            setFarmacia(dadosExtraidos);
            setDadosOriginais(dadosExtraidos);
            
            console.log('✅ Dados carregados:', {
              name: dadosExtraidos.name,
              cnpj: dadosExtraidos.cnpj,
              plano: dadosExtraidos._meta.plano
            });
          } else {
            console.warn('⚠️ Documento da farmácia não encontrado');
            exibirMensagem("Dados da farmácia não encontrados", "error");
          }
        } catch (error) {
          console.error("Erro ao carregar dados:", error);
          exibirMensagem("Erro ao carregar dados da farmácia", "error");
        } finally {
          setIsLoading(false);
          setIsInitialized(true);
        }
      } else if (currentUser && isInitialized) {
        setUser(currentUser);
      } else if (!currentUser) {
        navigate('/login');
      }
    });
    
    return () => unsubscribe();
  }, [isInitialized, navigate, exibirMensagem, auth]);

  // ============================================
  // HANDLERS DE ATUALIZAÇÃO MEMOIZADOS
  // ============================================

  const updateFarmaciaField = useCallback((field, value) => {
    setFarmacia(prev => ({ ...prev, [field]: value }));
  }, []);

  const updateAddressField = useCallback((field, value) => {
    setFarmacia(prev => ({
      ...prev,
      address: { ...prev.address, [field]: value }
    }));
  }, []);

  // Formatações memoizadas
  const formatCNPJ = useCallback((value) => {
    return value
      .replace(/\D/g, '')
      .replace(/(\d{2})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1/$2')
      .replace(/(\d{4})(\d)/, '$1-$2')
      .slice(0, 18);
  }, []);

  const formatPhone = useCallback((value) => {
    return value
      .replace(/\D/g, '')
      .replace(/(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{5})(\d)/, '$1-$2')
      .slice(0, 15);
  }, []);

  const formatCEP = useCallback((value) => {
    return value
      .replace(/\D/g, '')
      .replace(/(\d{5})(\d)/, '$1-$2')
      .slice(0, 9);
  }, []);

  // ============================================
  // SALVAR PERFIL
  // ✅ REFATORADO: Usa API do backend (PUT /profile)
  // O backend cuida de: WhatsApp, geocodificação, timestamps
  // ============================================
  const salvarPerfil = useCallback(async () => {
    if (!user) {
      exibirMensagem("Usuário não autenticado", "error");
      return;
    }

    if (!farmacia.name?.trim()) {
      exibirMensagem("Razão Social é obrigatória", "warning");
      return;
    }

    if (!farmacia.cnpj || farmacia.cnpj.replace(/\D/g, '').length !== 14) {
      exibirMensagem("CNPJ inválido", "warning");
      return;
    }

    if (!farmacia.crf?.trim()) {
      exibirMensagem("CRF é obrigatório", "warning");
      return;
    }

    setLoadingSalvar(true);
    
    try {
      // ✅ Preparar dados no formato esperado pelo backend
      const dadosParaAPI = prepararDadosParaAPI(farmacia);
      
      console.log('📤 Enviando dados para API:', dadosParaAPI);

      // ✅ Chamar API do backend - ele cuida de TUDO:
      // - Formatar WhatsApp (adicionar código 55)
      // - Regeocodificar endereço se mudou (cidade ou CEP)
      // - Atualizar timestamps
      const response = await fetchWithAuth(
        "https://api.scriptusrxia.com.br/api/v1/auth/pharmacy/profile",
        {
          method: "PUT",
          body: JSON.stringify(dadosParaAPI)
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error?.message || 'Erro ao salvar dados');
      }

      const resultado = await response.json();
      
      console.log('✅ Dados salvos via API:', resultado);
      
      // Atualizar dados originais para resetar indicador de mudanças
      setDadosOriginais({ ...farmacia });
      
      // Mensagem de sucesso
      exibirMensagem("Dados da farmácia atualizados com sucesso! 📍", "success");
      
    } catch (error) {
      console.error("Erro ao salvar perfil:", error);
      
      // Tratamento de erros específicos
      if (error.message?.includes('401') || error.message?.includes('token')) {
        exibirMensagem("Sessão expirada. Faça login novamente.", "error");
        setTimeout(() => navigate("/login"), 2000);
      } else {
        exibirMensagem(error.message || "Erro ao salvar dados da farmácia", "error");
      }
    } finally {
      setLoadingSalvar(false);
    }
  }, [user, farmacia, exibirMensagem, navigate]);

  // ============================================
  // ATUALIZAR LOGIN
  // ============================================
  const atualizarLogin = useCallback(async () => {
    if (!senhaAtual) {
      exibirMensagem("Digite sua senha atual para confirmar", "warning");
      return;
    }
  
    if (!user || !user.email) {
      exibirMensagem("Erro ao carregar usuário", "error");
      navigate("/login");
      return;
    }

    if (!novaSenha) {
      exibirMensagem("Digite a nova senha", "warning");
      return;
    }

    if (novaSenha.length < 6) {
      exibirMensagem("A nova senha deve ter pelo menos 6 caracteres", "warning");
      return;
    }
  
    setLoadingLogin(true);
    try {
      const credential = EmailAuthProvider.credential(user.email, senhaAtual);
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, novaSenha);
  
      exibirMensagem("Senha atualizada com sucesso!", "success");
      setNovaSenha("");
      setSenhaAtual("");
    } catch (error) {
      console.error("Erro ao atualizar senha:", error);
      
      if (error.code === "auth/requires-recent-login") {
        exibirMensagem("Por segurança, faça login novamente", "warning");
        setTimeout(() => navigate("/login"), 2000);
      } else if (error.code === "auth/wrong-password" || error.code === "auth/invalid-credential") {
        exibirMensagem("Senha atual incorreta", "error");
      } else if (error.code === "auth/weak-password") {
        exibirMensagem("Nova senha muito fraca", "warning");
      } else {
        exibirMensagem("Erro ao atualizar senha", "error");
      }
    } finally {
      setLoadingLogin(false);
    }
  }, [user, senhaAtual, novaSenha, exibirMensagem, navigate]);

  // ============================================
  // EXCLUIR CONTA
  // ============================================
  const excluirConta = useCallback(async () => {
    if (!senhaAtual) {
      exibirMensagem("Digite sua senha atual para confirmar exclusão", "warning");
      return;
    }
  
    const confirmacao = window.confirm(
      "⚠️ ATENÇÃO: Tem certeza que deseja excluir sua conta?\n\n" +
      "Esta ação é IRREVERSÍVEL e você perderá:\n" +
      "• Todos os dados cadastrais\n" +
      "• Histórico de dispensações\n" +
      "• Assinatura ativa (será cancelada)\n\n" +
      "Deseja continuar?"
    );
    
    if (!confirmacao) return;
  
    setLoadingExcluir(true);
  
    try {
      const credential = EmailAuthProvider.credential(user.email, senhaAtual);
      await reauthenticateWithCredential(user, credential);
  
      const isPartner = farmacia._meta?.plano === 'partner';
      const isAtivo = farmacia._meta?.statusAssinatura === 'ativa';
      
      if (isPartner && isAtivo) {
        try {
          console.log("Tentando cancelar assinatura...");
          const response = await fetchWithAuth(
            "https://api.scriptusrxia.com.br/api/v1/payment/pharmacy/cancel", 
            {
              method: "POST",
              body: JSON.stringify({ motivo: "exclusao_conta" })
            }
          );
      
          if (response.ok) {
            console.log("✅ Assinatura cancelada");
          }
        } catch (error) {
          console.warn("⚠️ Erro ao cancelar assinatura (continuando):", error.message);
        }
      }
  
      console.log("Deletando dados da farmácia...");
      await deleteDoc(doc(db, "pharmacies", user.uid));
      
      console.log("Deletando usuário do Firebase Auth...");
      await deleteUser(user);
      
      console.log("✅ Conta excluída com sucesso");
      await signOut(auth);
      navigate("/login");
  
    } catch (error) {
      console.error("Erro ao excluir conta:", error);
      
      if (error.code === "auth/requires-recent-login") {
        exibirMensagem("Por segurança, faça login novamente", "warning");
        setTimeout(() => navigate("/login"), 2000);
      } else if (error.code === "auth/wrong-password" || error.code === "auth/invalid-credential") {
        exibirMensagem("Senha incorreta", "error");
      } else {
        exibirMensagem(`Erro ao excluir conta: ${error.message}`, "error");
      }
    } finally {
      setLoadingExcluir(false);
    }
  }, [user, senhaAtual, farmacia._meta, exibirMensagem, navigate, auth]);

  // ============================================
  // CÁLCULO DE COMPLETUDE MEMOIZADO
  // ============================================
  const completude = useMemo(() => {
    const camposObrigatorios = [
      farmacia.name,
      farmacia.cnpj,
      farmacia.crf
    ];
    
    const camposOpcionais = [
      farmacia.tradeName,
      farmacia.phone,
      farmacia.afe,
      farmacia.address?.street,
      farmacia.address?.number,
      farmacia.address?.neighborhood,
      farmacia.address?.city,
      farmacia.address?.state,
      farmacia.address?.zipCode
    ];
    
    const obrigatoriosPreenchidos = camposObrigatorios.filter(c => c && c.trim() !== '').length;
    const opcionaisPreenchidos = camposOpcionais.filter(c => c && c.trim() !== '').length;
    
    const pesoObrigatorios = (obrigatoriosPreenchidos / camposObrigatorios.length) * 60;
    const pesoOpcionais = (opcionaisPreenchidos / camposOpcionais.length) * 40;
    
    return Math.round(pesoObrigatorios + pesoOpcionais);
  }, [farmacia]);

  // Verificar mudanças
  const temMudancas = useMemo(() => {
    if (!dadosOriginais) return false;
    return JSON.stringify(farmacia) !== JSON.stringify(dadosOriginais);
  }, [farmacia, dadosOriginais]);

  // Seções memoizadas
  const secoes = useMemo(() => [
    {
      id: "perfil",
      titulo: "Dados da Farmácia",
      icone: <Building2 className="w-3 h-3" />,
      color: "emerald"
    },
    {
      id: "acesso",
      titulo: "Configurações de Acesso",
      icone: <Lock className="w-3 h-3" />,
      color: "amber"
    },
    {
      id: "excluir",
      titulo: "Encerrar Conta",
      icone: <Trash2 className="w-3 h-3" />,
      color: "red"
    }
  ], []);

  // ============================================
  // COMPONENTES MEMOIZADOS
  // ============================================
  
  const SidebarContent = useMemo(() => (
    <>
      {/* Navegação */}
      <div className="space-y-1.5 mb-5">
        {secoes.map((secao) => (
          <button
            key={secao.id}
            onClick={() => setSecaoSelecionada(secao.id)}
            className={`w-full text-left px-3 py-2.5 rounded-xl sf-pro-text font-semibold text-xs transition-all duration-200 flex items-center space-x-2.5 ${
              secaoSelecionada === secao.id
                ? secao.color === "emerald"
                  ? "bg-emerald-600 text-white shadow-lg"
                  : secao.color === "amber"
                  ? "bg-amber-500 text-white shadow-lg"
                  : "bg-red-600 text-white shadow-lg"
                : "text-slate-600 hover:text-slate-900 hover:bg-white/50"
            }`}
          >
            <div className={`p-1 rounded-lg ${
              secaoSelecionada === secao.id 
                ? "bg-white/20" 
                : "bg-slate-100/60"
            }`}>
              {secao.icone}
            </div>
            <span>{secao.titulo}</span>
          </button>
        ))}
      </div>

      {/* Card de Completude */}
      <div className="glass-card-refined rounded-xl p-3.5 mb-4">
        <div className="flex items-center space-x-1.5 mb-2">
          <div className={`p-0.5 rounded-lg ${
            completude === 100 
              ? 'bg-emerald-100 border border-emerald-200' 
              : 'bg-amber-100 border border-amber-200'
          }`}>
            {completude === 100 
              ? <CheckCircle2 className="w-2.5 h-2.5 text-emerald-600" />
              : <Shield className="w-2.5 h-2.5 text-amber-600" />
            }
          </div>
          <h3 className="sf-pro-text font-semibold text-slate-900 text-xs">
            Cadastro SNGPC
          </h3>
        </div>
        <div className="mb-1.5">
          <div className="flex justify-between text-[10px] sf-pro-text mb-1">
            <span className="text-slate-600">Completude</span>
            <span className={`font-semibold ${
              completude === 100 ? 'text-emerald-600' : 'text-amber-600'
            }`}>{completude}%</span>
          </div>
          <div className="w-full bg-slate-200 rounded-full h-1.5">
            <div 
              className={`h-1.5 rounded-full transition-all duration-500 ${
                completude === 100 ? 'bg-emerald-500' : 'bg-amber-500'
              }`}
              style={{ width: `${completude}%` }}
            ></div>
          </div>
        </div>
        <p className="sf-pro-text text-slate-500 text-[10px] leading-relaxed">
          {completude === 100 
            ? '✅ Cadastro completo para SNGPC!'
            : completude >= 60
            ? '⚠️ Preencha o endereço para XMLs completos'
            : '⚠️ Complete os campos obrigatórios'
          }
        </p>
      </div>

      {/* Card Info Plano */}
      <div className={`rounded-xl p-3.5 mb-4 border ${
        farmacia._meta?.plano === 'partner'
          ? 'bg-gradient-to-br from-purple-50 to-violet-50 border-purple-200'
          : 'bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-200'
      }`}>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center space-x-1.5">
            <div className={`p-1 rounded-lg ${
              farmacia._meta?.plano === 'partner' ? 'bg-purple-100' : 'bg-emerald-100'
            }`}>
              {farmacia._meta?.plano === 'partner' ? (
                <svg className="w-3 h-3 text-purple-600" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"/>
                </svg>
              ) : (
                <svg className="w-3 h-3 text-emerald-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                </svg>
              )}
            </div>
            <span className="sf-pro-text font-semibold text-slate-700 text-[10px] uppercase tracking-wide">
              Seu Plano
            </span>
          </div>
          {farmacia._meta?.plano === 'partner' && (
            <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 text-[9px] font-bold rounded-full uppercase">
              Pro
            </span>
          )}
        </div>

        <div className="flex items-center space-x-1.5 mb-2">
          {farmacia._meta?.plano === 'partner' ? (
            <svg className="w-4 h-4 text-purple-500" viewBox="0 0 24 24" fill="currentColor">
              <path d="M5 16L3 5L8.5 10L12 4L15.5 10L21 5L19 16H5M19 19C19 19.6 18.6 20 18 20H6C5.4 20 5 19.6 5 19V18H19V19Z"/>
            </svg>
          ) : (
            <svg className="w-4 h-4 text-emerald-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
          )}
          <span className={`sf-pro-display font-bold text-sm ${
            farmacia._meta?.plano === 'partner' ? 'text-purple-700' : 'text-emerald-700'
          }`}>
            {farmacia._meta?.plano === 'partner' ? 'Partner' : 'Basic'}
          </span>
        </div>

        <div className="flex items-center space-x-1.5">
          {farmacia._meta?.statusAssinatura === 'ativa' ? (
            <>
              <span className="relative flex h-1.5 w-1.5">
                <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${
                  farmacia._meta?.plano === 'partner' ? 'bg-purple-400' : 'bg-emerald-400'
                }`}></span>
                <span className={`relative inline-flex rounded-full h-1.5 w-1.5 ${
                  farmacia._meta?.plano === 'partner' ? 'bg-purple-500' : 'bg-emerald-500'
                }`}></span>
              </span>
              <span className={`sf-pro-text text-[10px] font-semibold ${
                farmacia._meta?.plano === 'partner' ? 'text-purple-600' : 'text-emerald-600'
              }`}>
                Ativo
              </span>
            </>
          ) : (
            <>
              <svg className="w-2.5 h-2.5 text-amber-500" viewBox="0 0 24 24" fill="currentColor">
                <circle cx="12" cy="12" r="10"/>
              </svg>
              <span className="sf-pro-text text-[10px] font-semibold text-amber-600">
                {farmacia._meta?.statusAssinatura || 'Verificando...'}
              </span>
            </>
          )}
        </div>

        {farmacia._meta?.plano !== 'partner' && (
          <div className="mt-2.5 pt-2.5 border-t border-emerald-200">
            <a 
              href="/assinatura" 
              className="flex items-center justify-between group"
            >
              <span className="sf-pro-text text-[10px] text-slate-500 group-hover:text-purple-600 transition-colors">
                Upgrade para Partner
              </span>
              <svg className="w-3 h-3 text-slate-400 group-hover:text-purple-500 transition-colors" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            </a>
          </div>
        )}
      </div>

      {/* Card Info */}
      <div className="glass-card-refined rounded-xl p-3.5">
        <div className="flex items-center space-x-1.5 mb-2">
          <div className="p-0.5 rounded-lg bg-blue-100 border border-blue-200">
            <Info className="w-2.5 h-2.5 text-blue-600" />
          </div>
          <h3 className="sf-pro-text font-semibold text-slate-900 text-xs">
            Por que preencher?
          </h3>
        </div>
        <p className="sf-pro-text text-slate-700 text-[10px] leading-relaxed">
          Os dados cadastrais são usados para gerar automaticamente o XML SNGPC conforme exigências da ANVISA.
        </p>
      </div>
    </>
  ), [secoes, secaoSelecionada, completude, farmacia._meta]);

  // ============================================
  // LOADING STATE
  // ============================================
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-refined flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-6 h-6 animate-spin text-emerald-600 mx-auto mb-3" />
          <p className="sf-pro-text text-slate-600 text-xs">Carregando configurações...</p>
        </div>
      </div>
    );
  }
  
  // ============================================
  // RENDER
  // ============================================
  return (
    <div className="min-h-screen bg-gradient-refined relative overflow-x-hidden">
      
      {/* Background Pattern */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        <div className="background-pattern-refined"></div>
        <div className="background-glow-1"></div>
        <div className="background-glow-2"></div>
      </div>

      {/* Overlay para sidebar mobile */}
      {sidebarMobile && (
        <div 
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-30 lg:hidden"
          onClick={() => setSidebarMobile(false)}
        />
      )}

      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-40 transition-all duration-500 ${
        scrolled ? 'header-scrolled-refined' : 'header-refined'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setSidebarMobile(!sidebarMobile)}
              className="lg:hidden w-7 h-7 bg-white hover:bg-slate-50 rounded-lg border border-slate-200 flex items-center justify-center shadow-sm transition-all duration-200"
            >
              <Menu className="w-3 h-3 text-slate-600" />
            </button>

            <a href="/dashboard" className="logo-link-refined">
              <img src="/logo.PNG" alt="Scriptus RxIA" className="h-5 w-auto" />
            </a>
            
            <HeaderMenu />
          </div>
        </div>
      </header>

      {/* Sidebar Desktop */}
      <div className="hidden lg:block fixed top-0 left-0 bottom-0 w-64 z-20">
        <div className="h-full glass-card-elevated-refined border-r border-slate-200/50">
          <div className="p-5 h-full overflow-y-auto pt-28">
            {SidebarContent}
          </div>
        </div>
      </div>

      {/* Sidebar Mobile */}
      <div className={`lg:hidden fixed top-0 left-0 bottom-0 w-64 z-50 transform transition-transform duration-300 ${
        sidebarMobile ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="h-full glass-card-elevated-refined border-r border-slate-200/50">
          <div className="p-4 h-full overflow-y-auto pt-20">
            <button
              onClick={() => setSidebarMobile(false)}
              className="absolute top-3 right-3 w-6 h-6 bg-red-100 hover:bg-red-200 text-red-600 rounded-lg border border-red-200 transition-all duration-200 flex items-center justify-center shadow-sm"
            >
              <X className="w-3 h-3" />
            </button>

            {SidebarContent}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="lg:pl-64">
        <div className="pt-20 sm:pt-24 pb-8 px-3 sm:px-6 relative z-10">
          <div className="max-w-4xl mx-auto space-y-6">

            {/* SEÇÃO: DADOS DA FARMÁCIA */}
            {secaoSelecionada === "perfil" && (
              <div className="space-y-6">
                <div className="text-center mb-6 sm:mb-8">
                  <h1 className="text-xl sm:text-2xl sf-pro-display font-medium text-slate-900 tracking-tight mb-1.5">
                    Dados da Farmácia
                  </h1>
                  <p className="text-xs sm:text-sm sf-pro-text text-slate-500">
                    Informações cadastrais usadas na geração do XML SNGPC
                  </p>
                  {temMudancas && (
                    <p className="text-[10px] sf-pro-text text-amber-600 mt-1.5">
                      ⚠️ Você tem alterações não salvas
                    </p>
                  )}
                </div>

                {/* Card Informações Básicas */}
                <div className="glass-card-elevated-refined rounded-2xl p-5 sm:p-6 shadow-xl">
                  <div className="flex items-center space-x-3 mb-5">
                    <div className="main-icon-refined bg-gradient-to-br from-slate-100 to-slate-50 border-2 border-slate-200/50">
                      <Building2 className="w-4 h-4 text-slate-600" />
                    </div>
                    <h2 className="text-lg sf-pro-display font-semibold text-slate-900">
                      Informações Cadastrais
                    </h2>
                  </div>

                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <InputCustom
                        label="Razão Social"
                        placeholder="Razão social da farmácia"
                        value={farmacia.name || ""}
                        onChange={(e) => updateFarmaciaField('name', e.target.value)}
                        icon={Building}
                        required
                      />

                      <InputCustom
                        label="Nome Fantasia"
                        placeholder="Nome fantasia"
                        value={farmacia.tradeName || ""}
                        onChange={(e) => updateFarmaciaField('tradeName', e.target.value)}
                        icon={Building2}
                        hint="Opcional - usa Razão Social se não preenchido"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-1.5">
                        <label className="block sf-pro-text text-xs font-medium text-slate-700">
                          CNPJ <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                          <input
                            type="text"
                            placeholder="00.000.000/0000-00"
                            value={farmacia.cnpj || ""}
                            onChange={(e) => updateFarmaciaField('cnpj', formatCNPJ(e.target.value))}
                            className="input-glass-refined w-full pl-9"
                          />
                        </div>
                      </div>

                      <div className="space-y-1.5">
                        <label className="block sf-pro-text text-xs font-medium text-slate-700">
                          CRF do Responsável <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <FileCheck className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                          <input
                            type="text"
                            placeholder="12345-UF"
                            value={farmacia.crf || ""}
                            onChange={(e) => updateFarmaciaField('crf', e.target.value.toUpperCase())}
                            className="input-glass-refined w-full pl-9"
                          />
                        </div>
                        <p className="text-[10px] text-slate-500 sf-pro-text">Ex: 33893-GO</p>
                      </div>

                      <div className="space-y-1.5">
                        <label className="block sf-pro-text text-xs font-medium text-slate-700">
                          AFE (ANVISA)
                        </label>
                        <div className="relative">
                          <Shield className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                          <input
                            type="text"
                            placeholder="Nº Autorização"
                            value={farmacia.afe || ""}
                            onChange={(e) => updateFarmaciaField('afe', e.target.value)}
                            className="input-glass-refined w-full pl-9"
                          />
                        </div>
                        <p className="text-[10px] text-slate-500 sf-pro-text">Autorização de Funcionamento</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <label className="block sf-pro-text text-xs font-medium text-slate-700">
                          Telefone
                        </label>
                        <div className="relative">
                          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                          <input
                            type="text"
                            placeholder="(11) 99999-9999"
                            value={farmacia.phone || ""}
                            onChange={(e) => updateFarmaciaField('phone', formatPhone(e.target.value))}
                            className="input-glass-refined w-full pl-9"
                          />
                        </div>
                      </div>

                      <div className="space-y-1.5">
                        <label className="block sf-pro-text text-xs font-medium text-slate-700">
                          Email
                        </label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                          <input
                            type="email"
                            value={user?.email || farmacia._meta?.email || ""}
                            disabled
                            className="input-glass-refined w-full pl-9 opacity-60 cursor-not-allowed"
                          />
                        </div>
                        <p className="text-[10px] text-slate-500 sf-pro-text">
                          Email não pode ser alterado
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Card Endereço */}
                <div className="glass-card-elevated-refined rounded-2xl p-5 sm:p-6 shadow-xl">
                  <div className="flex items-center space-x-3 mb-5">
                    <div className="main-icon-refined bg-gradient-to-br from-blue-100 to-blue-50 border-2 border-blue-200/50">
                      <MapPin className="w-4 h-4 text-blue-600" />
                    </div>
                    <div>
                      <h2 className="text-lg sf-pro-display font-semibold text-slate-900">
                        Endereço
                      </h2>
                      {/* ✅ NOVO: Hint sobre geolocalização */}
                      <p className="text-[10px] text-slate-500 sf-pro-text">
                        📍 A localização no mapa será atualizada automaticamente ao salvar
                      </p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-1.5">
                        <label className="block sf-pro-text text-xs font-medium text-slate-700">
                          CEP
                        </label>
                        <div className="relative">
                          <Map className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                          <input
                            type="text"
                            placeholder="00000-000"
                            value={farmacia.address?.zipCode || ""}
                            onChange={(e) => updateAddressField('zipCode', formatCEP(e.target.value))}
                            className="input-glass-refined w-full pl-9"
                          />
                        </div>
                        <p className="text-[10px] text-slate-500 sf-pro-text">Digite o CEP no formato 00000-000</p>
                      </div>

                      <div className="md:col-span-2">
                        <InputCustom
                          label="Logradouro"
                          placeholder="Rua, Avenida, etc."
                          value={farmacia.address?.street || ""}
                          onChange={(e) => updateAddressField('street', e.target.value)}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <InputCustom
                        label="Número"
                        placeholder="Nº"
                        value={farmacia.address?.number || ""}
                        onChange={(e) => updateAddressField('number', e.target.value)}
                      />

                      <InputCustom
                        label="Complemento"
                        placeholder="Sala, Loja..."
                        value={farmacia.address?.complement || ""}
                        onChange={(e) => updateAddressField('complement', e.target.value)}
                      />

                      <div className="md:col-span-2">
                        <InputCustom
                          label="Bairro"
                          placeholder="Bairro"
                          value={farmacia.address?.neighborhood || ""}
                          onChange={(e) => updateAddressField('neighborhood', e.target.value)}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="md:col-span-2">
                        <InputCustom
                          label="Cidade"
                          placeholder="Cidade"
                          value={farmacia.address?.city || ""}
                          onChange={(e) => updateAddressField('city', e.target.value)}
                        />
                      </div>

                      <div className="space-y-1.5">
                        <label className="block sf-pro-text text-xs font-medium text-slate-700">
                          UF
                        </label>
                        <select
                          value={farmacia.address?.state || ""}
                          onChange={(e) => updateAddressField('state', e.target.value)}
                          className="input-glass-refined w-full"
                        >
                          <option value="">Selecione</option>
                          {UF_LIST.map(uf => (
                            <option key={uf} value={uf}>{uf}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Save Button */}
                <div className="pt-2">
                  <BotaoCustom
                    onClick={salvarPerfil}
                    disabled={loadingSalvar}
                    loading={loadingSalvar}
                    variant="primary"
                  >
                    {loadingSalvar ? (
                      <span className="sf-pro-text text-xs">Salvando...</span>
                    ) : (
                      <>
                        <Save className="w-3.5 h-3.5" />
                        <span className="sf-pro-text text-xs">
                          {temMudancas ? 'Salvar Alterações' : 'Salvar Dados'}
                        </span>
                      </>
                    )}
                  </BotaoCustom>
                </div>
              </div>
            )}

            {/* SEÇÃO: CONFIGURAÇÕES DE ACESSO */}
            {secaoSelecionada === "acesso" && (
              <div className="space-y-6">
                <div className="text-center mb-6 sm:mb-8">
                  <h1 className="text-xl sm:text-2xl sf-pro-display font-medium text-slate-900 tracking-tight mb-1.5">
                    Configurações de Acesso
                  </h1>
                  <p className="text-xs sm:text-sm sf-pro-text text-slate-500">
                    Atualize sua senha de login para manter sua conta segura
                  </p>
                </div>

                <div className="glass-card-elevated-refined rounded-2xl p-5 sm:p-6 shadow-xl">
                  <div className="flex items-center space-x-3 mb-5">
                    <div className="main-icon-refined bg-gradient-to-br from-amber-100 to-amber-50 border-2 border-amber-200/50">
                      <Lock className="w-4 h-4 text-amber-600" />
                    </div>
                    <h2 className="text-lg sf-pro-display font-semibold text-slate-900">
                      Alterar Senha
                    </h2>
                  </div>

                  <div className="space-y-4">
                    <div className="glass-card-refined rounded-xl p-3.5 bg-amber-50/50 border border-amber-200">
                      <div className="flex items-start gap-2.5">
                        <div className="p-1 rounded-lg bg-amber-100">
                          <Shield className="w-3 h-3 text-amber-600" />
                        </div>
                        <div>
                          <p className="text-amber-900 sf-pro-text font-semibold mb-0.5 text-xs">
                            Atenção
                          </p>
                          <p className="text-amber-800 sf-pro-text text-[10px] leading-relaxed">
                            Sua senha atual é necessária para confirmar qualquer alteração de segurança.
                          </p>
                        </div>
                      </div>
                    </div>

                    <InputCustom
                      label="Nova senha"
                      type="password"
                      placeholder="Mínimo 6 caracteres"
                      value={novaSenha}
                      onChange={(e) => setNovaSenha(e.target.value)}
                      hint="A nova senha deve ter pelo menos 6 caracteres"
                    />

                    <InputCustom
                      label="Senha atual (obrigatória)"
                      type="password"
                      placeholder="Sua senha atual"
                      value={senhaAtual}
                      onChange={(e) => setSenhaAtual(e.target.value)}
                      required
                    />

                    <div className="pt-2">
                      <BotaoCustom
                        onClick={atualizarLogin}
                        disabled={loadingLogin || !senhaAtual || !novaSenha}
                        loading={loadingLogin}
                        variant="warning"
                      >
                        {loadingLogin ? (
                          <span className="sf-pro-text text-xs">Atualizando...</span>
                        ) : (
                          <>
                            <Shield className="w-3.5 h-3.5" />
                            <span className="sf-pro-text text-xs">Atualizar Senha</span>
                          </>
                        )}
                      </BotaoCustom>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* SEÇÃO: ENCERRAR CONTA */}
            {secaoSelecionada === "excluir" && (
              <div className="space-y-6">
                <div className="text-center mb-6 sm:mb-8">
                  <h1 className="text-xl sm:text-2xl sf-pro-display font-medium text-slate-900 tracking-tight mb-1.5">
                    Encerrar Conta
                  </h1>
                  <p className="text-xs sm:text-sm sf-pro-text text-slate-500">
                    Excluir permanentemente sua conta Scriptus Pharma Partner
                  </p>
                </div>

                <div className="glass-card-elevated-refined rounded-2xl p-5 sm:p-6 bg-red-50/30 border-2 border-red-200/50 shadow-xl">
                  <div className="flex items-start space-x-3">
                    <div className="main-icon-refined bg-gradient-to-br from-red-100 to-red-50 border-2 border-red-200/50">
                      <AlertTriangle className="w-4 h-4 text-red-600" />
                    </div>
                    <div>
                      <h3 className="text-red-900 sf-pro-display font-semibold text-sm mb-2">
                        Atenção: Ação Irreversível
                      </h3>
                      <p className="text-red-800 sf-pro-text leading-relaxed mb-2 text-xs">
                        Ao excluir sua conta, todos os seus dados serão permanentemente removidos:
                      </p>
                      <ul className="text-red-800 sf-pro-text space-y-1 text-xs">
                        <li className="flex items-center gap-1.5">
                          <div className="w-1 h-1 bg-red-600 rounded-full"></div>
                          Dados cadastrais da farmácia
                        </li>
                        <li className="flex items-center gap-1.5">
                          <div className="w-1 h-1 bg-red-600 rounded-full"></div>
                          Histórico de dispensações
                        </li>
                        <li className="flex items-center gap-1.5">
                          <div className="w-1 h-1 bg-red-600 rounded-full"></div>
                          Assinatura ativa (será cancelada)
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="glass-card-elevated-refined rounded-2xl p-5 sm:p-6 shadow-xl">
                  <div className="flex items-center space-x-3 mb-5">
                    <div className="main-icon-refined bg-gradient-to-br from-red-100 to-red-50 border-2 border-red-200/50">
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </div>
                    <h2 className="text-lg sf-pro-display font-semibold text-slate-900">
                      Confirmação de Exclusão
                    </h2>
                  </div>

                  <div className="space-y-4">
                    <InputCustom
                      label="Confirme sua senha atual"
                      type="password"
                      placeholder="Sua senha atual"
                      value={senhaAtual}
                      onChange={(e) => setSenhaAtual(e.target.value)}
                      required
                    />

                    <div className="pt-2">
                      <BotaoCustom
                        onClick={excluirConta}
                        disabled={loadingExcluir || !senhaAtual}
                        loading={loadingExcluir}
                        variant="danger"
                      >
                        {loadingExcluir ? (
                          <span className="sf-pro-text text-xs">Excluindo...</span>
                        ) : (
                          <>
                            <Trash2 className="w-3.5 h-3.5" />
                            <span className="sf-pro-text text-xs">Excluir Minha Conta</span>
                          </>
                        )}
                      </BotaoCustom>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Notification */}
      {mensagem && (
        <div className="fixed bottom-6 right-6 left-6 sm:left-auto z-50 max-w-sm">
          <div className={`notification-refined ${
            tipoMensagem === "error"
              ? "notification-error-refined"
              : tipoMensagem === "warning"
              ? "notification-warning-refined"
              : "notification-success-refined"
          }`}>
            <div className="flex items-center gap-3">
              <div className="notification-icon-refined">
                {tipoMensagem === "error" ? (
                  <AlertTriangle className="w-4 h-4 text-red-600" />
                ) : tipoMensagem === "warning" ? (
                  <AlertTriangle className="w-4 h-4 text-amber-600" />
                ) : (
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                )}
              </div>
              <span className="sf-pro-text font-medium text-slate-900 text-xs flex-1">
                {mensagem}
              </span>
              <button 
                onClick={() => setMensagem("")} 
                className="notification-close-refined"
              >
                <X className="w-3.5 h-3.5 text-slate-400 hover:text-slate-600" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Styles */}
      <style>{styles}</style>
    </div>
  );
};

// ============================================
// STYLES - GLASSMORPHISM PREMIUM REFINADO
// ============================================
const styles = `
  /* ===== TIPOGRAFIA ===== */
  .sf-pro-display {
    font-family: -apple-system, "SF Pro Display", BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }
  
  .sf-pro-text {
    font-family: -apple-system, "SF Pro Text", BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  /* ===== BACKGROUND ===== */
  .bg-gradient-refined {
    background: linear-gradient(135deg, 
      #f8fafc 0%, 
      #ffffff 25%, 
      #f1f5f9 50%, 
      #ffffff 75%, 
      #ecfdf5 100%);
  }

  .background-pattern-refined {
    position: absolute;
    inset: 0;
    background-image: 
      radial-gradient(circle at 25% 25%, rgba(16, 185, 129, 0.08) 0%, transparent 50%),
      radial-gradient(circle at 75% 75%, rgba(20, 184, 166, 0.06) 0%, transparent 50%);
    opacity: 0.7;
  }

  .background-glow-1 {
    position: absolute;
    top: 10%;
    left: 15%;
    width: 500px;
    height: 500px;
    background: radial-gradient(circle, rgba(16, 185, 129, 0.08) 0%, transparent 70%);
    border-radius: 50%;
    filter: blur(40px);
  }

  .background-glow-2 {
    position: absolute;
    bottom: 15%;
    right: 20%;
    width: 400px;
    height: 400px;
    background: radial-gradient(circle, rgba(99, 102, 241, 0.04) 0%, transparent 70%);
    border-radius: 50%;
    filter: blur(35px);
  }

  /* ===== HEADER ===== */
  .header-refined {
    background: rgba(255, 255, 255, 0.75);
    backdrop-filter: blur(24px) saturate(180%);
    -webkit-backdrop-filter: blur(24px) saturate(180%);
    border-bottom: 1px solid rgba(255, 255, 255, 0.4);
  }

  .header-scrolled-refined {
    background: rgba(255, 255, 255, 0.90);
    backdrop-filter: blur(30px) saturate(200%);
    -webkit-backdrop-filter: blur(30px) saturate(200%);
    border-bottom: 1px solid rgba(255, 255, 255, 0.6);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06);
  }

  .logo-link-refined {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }
  .logo-link-refined:hover {
    opacity: 0.8;
    transform: scale(1.05);
  }

  /* ===== GLASS CARDS ===== */
  .glass-card-refined {
    background: rgba(255, 255, 255, 0.80);
    backdrop-filter: blur(24px) saturate(180%);
    -webkit-backdrop-filter: blur(24px) saturate(180%);
    border: 1px solid rgba(255, 255, 255, 0.5);
    box-shadow: 
      0 8px 32px rgba(0, 0, 0, 0.06),
      0 4px 16px rgba(0, 0, 0, 0.04),
      inset 0 1px 0 rgba(255, 255, 255, 0.9);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .glass-card-elevated-refined {
    background: rgba(255, 255, 255, 0.90);
    backdrop-filter: blur(30px) saturate(200%);
    -webkit-backdrop-filter: blur(30px) saturate(200%);
    border: 1px solid rgba(255, 255, 255, 0.6);
    box-shadow: 
      0 25px 50px rgba(0, 0, 0, 0.08),
      0 12px 24px rgba(0, 0, 0, 0.06),
      0 6px 12px rgba(0, 0, 0, 0.04),
      inset 0 2px 0 rgba(255, 255, 255, 0.95);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }

  /* ===== ICONS ===== */
  .main-icon-refined {
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 14px;
    box-shadow: 
      0 8px 24px rgba(0, 0, 0, 0.08),
      0 4px 12px rgba(0, 0, 0, 0.04),
      inset 0 2px 0 rgba(255, 255, 255, 0.9);
    flex-shrink: 0;
  }

  /* ===== INPUTS ===== */
  .input-glass-refined {
    padding: 10px 14px;
    background: rgba(255, 255, 255, 0.90);
    border: 1px solid rgba(255, 255, 255, 0.6);
    border-radius: 12px;
    backdrop-filter: blur(20px) saturate(180%);
    -webkit-backdrop-filter: blur(20px) saturate(180%);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    font-family: -apple-system, "SF Pro Text", BlinkMacSystemFont, sans-serif;
    font-size: 12px;
    color: #1e293b;
    box-shadow: 
      0 4px 12px rgba(0, 0, 0, 0.04),
      inset 0 1px 0 rgba(255, 255, 255, 0.8);
  }

  .input-glass-refined::placeholder { 
    color: #94a3b8; 
  }

  .input-glass-refined:focus {
    outline: none;
    background: white;
    border-color: rgba(16, 185, 129, 0.5);
    box-shadow: 
      0 0 0 4px rgba(16, 185, 129, 0.1), 
      0 8px 24px rgba(0, 0, 0, 0.08);
    transform: translateY(-1px);
  }

  select.input-glass-refined {
    cursor: pointer;
    appearance: none;
    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%2394a3b8' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e");
    background-position: right 12px center;
    background-repeat: no-repeat;
    background-size: 14px;
    padding-right: 36px;
  }

  /* ===== BUTTONS ===== */
  .button-primary-refined {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 11px 20px;
    border: none;
    border-radius: 12px;
    color: white;
    font-weight: 700;
    font-size: 0.75rem;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 
      0 8px 24px rgba(0, 0, 0, 0.15),
      0 4px 12px rgba(0, 0, 0, 0.1),
      inset 0 1px 0 rgba(255, 255, 255, 0.2);
    cursor: pointer;
    will-change: transform;
  }

  .button-primary-refined:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 
      0 12px 32px rgba(0, 0, 0, 0.2),
      0 6px 16px rgba(0, 0, 0, 0.15);
  }

  .button-primary-refined:active:not(:disabled) { 
    transform: translateY(0); 
  }

  .button-primary-refined:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  /* ===== NOTIFICATIONS ===== */
  .notification-refined {
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(24px) saturate(200%);
    -webkit-backdrop-filter: blur(24px) saturate(200%);
    border-radius: 14px;
    padding: 14px 18px;
    animation: slideInUp 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .notification-success-refined {
    border: 1px solid rgba(16, 185, 129, 0.3);
    box-shadow: 
      0 20px 60px rgba(16, 185, 129, 0.15),
      0 10px 30px rgba(16, 185, 129, 0.1);
  }

  .notification-error-refined {
    border: 1px solid rgba(239, 68, 68, 0.3);
    box-shadow: 
      0 20px 60px rgba(239, 68, 68, 0.15),
      0 10px 30px rgba(239, 68, 68, 0.1);
  }

  .notification-warning-refined {
    border: 1px solid rgba(245, 158, 11, 0.3);
    box-shadow: 
      0 20px 60px rgba(245, 158, 11, 0.15),
      0 10px 30px rgba(245, 158, 11, 0.1);
  }

  .notification-icon-refined {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.8);
    border-radius: 9px;
    border: 1px solid rgba(255, 255, 255, 0.6);
    flex-shrink: 0;
  }

  .notification-close-refined {
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.6);
    border: 1px solid rgba(255, 255, 255, 0.4);
    border-radius: 7px;
    transition: all 0.3s ease;
    cursor: pointer;
  }

  .notification-close-refined:hover {
    background: rgba(255, 255, 255, 0.9);
  }

  /* ===== ANIMATIONS ===== */
  @keyframes slideInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes ping {
    75%, 100% {
      transform: scale(2);
      opacity: 0;
    }
  }

  .animate-ping {
    animation: ping 1.5s cubic-bezier(0, 0, 0.2, 1) infinite;
  }

  .animate-spin {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  /* ===== RESPONSIVE ===== */
  @media (max-width: 768px) {
    .button-primary-refined {
      padding: 10px 16px;
      font-size: 0.7rem;
    }

    .main-icon-refined {
      width: 36px;
      height: 36px;
    }

    .input-glass-refined {
      font-size: 14px;
      padding: 9px 12px;
    }
  }

  /* ===== ACCESSIBILITY ===== */
  @media (prefers-reduced-motion: reduce) {
    *, *::before, *::after {
      animation-duration: 0.01ms !important;
      transition-duration: 0.01ms !important;
    }
  }

  @media (prefers-contrast: high) {
    .glass-card-refined, 
    .glass-card-elevated-refined {
      background: rgba(255, 255, 255, 0.95);
      border: 2px solid rgba(0, 0, 0, 0.2);
    }
  }

  @media (pointer: coarse) {
    button, a, [role="button"] { 
      min-height: 44px; 
      min-width: 44px; 
    }
  }
`;

export default Configuracoes;